import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import adminService from '../services/adminService';
import Header from './common/Header';
import styles from '../styles/AdminPage.module.css';

const ZODIAC_SIGNS = [
  'ARIES', 'TAURUS', 'GEMINI', 'CANCER',
  'LEO', 'VIRGO', 'LIBRA', 'SCORPIO',
  'SAGITTARIUS', 'CAPRICORN', 'AQUARIUS', 'PISCES'
];

const ZODIAC_SYMBOLS = {
  ARIES: '♈', TAURUS: '♉', GEMINI: '♊', CANCER: '♋',
  LEO: '♌', VIRGO: '♍', LIBRA: '♎', SCORPIO: '♏',
  SAGITTARIUS: '♐', CAPRICORN: '♑', AQUARIUS: '♒', PISCES: '♓'
};

const BOOKING_STATUSES = ['PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELLED'];
const PERIODS = ['DAILY', 'WEEKLY', 'MONTHLY'];

const CATEGORY_LABELS = [
  { key: 'finance',     label: 'Finance' },
  { key: 'career',      label: 'Career' },
  { key: 'family',      label: 'Family' },
  { key: 'health',      label: 'Health' },
  { key: 'loveLife',    label: 'Love Life' },
  { key: 'marriedLife', label: 'Married Life' },
];
const EMPTY_CATS = { finance: '', career: '', family: '', health: '', loveLife: '', marriedLife: '' };

function Badge({ label }) {
  const map = {
    PENDING: styles.badgePending,
    CONFIRMED: styles.badgeConfirmed,
    COMPLETED: styles.badgeCompleted,
    CANCELLED: styles.badgeCancelled,
    PAID: styles.badgePaid,
    FAILED: styles.badgeFailed,
    REFUNDED: styles.badgeRefunded,
    WHATSAPP: styles.badgeWhatsapp,
    'LANDING PAGE': styles.badgeLandingPage,
    TESTING: styles.badgeTesting,
    APP: styles.badgeDefault,
  };
  return (
    <span className={`${styles.badge} ${map[label] || styles.badgeDefault}`}>
      {label}
    </span>
  );
}

function LoadingState({ text }) {
  return (
    <div className={styles.loadingState}>
      <div className={styles.spinner} />
      <p className={styles.loadingText}>{text}</p>
    </div>
  );
}

// ── Bookings Panel ────────────────────────────────────────────────────────────

function BookingsPanel() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getAllBookings();
      setBookings(Array.isArray(data?.bookings) ? data.bookings : []);
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id, status) => {
    setUpdatingId(id);
    try {
      await adminService.updateBookingStatus(id, status);
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status } : b));
    } catch {
      // silently ignore
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booking permanently?')) return;
    setDeletingId(id);
    try {
      await adminService.deleteBooking(id);
      setBookings(prev => prev.filter(b => b.id !== id));
    } catch {
      // silently ignore
    } finally {
      setDeletingId(null);
    }
  };

  const visible = filter === 'ALL' ? bookings : bookings.filter(b => b.status === filter);

  if (loading) return <LoadingState text="Loading bookings…" />;

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Bookings</h2>
        <div className={styles.filtersRow}>
          <span className={styles.countText}>{visible.length} booking{visible.length !== 1 ? 's' : ''}</span>
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className={styles.select}
          >
            <option value="ALL">All Statuses</option>
            {BOOKING_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {visible.length === 0 ? (
        <p className={styles.emptyState}>No bookings found.</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email / Phone</th>
                <th>Date &amp; Slot</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Source</th>
                <th>Meet Link</th>
                <th>Update Status</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(b => (
                <tr key={b.id}>
                  <td className={styles.tdName}>{b.name}</td>
                  <td>
                    <div>{b.email}</div>
                    <div className={styles.tdSub}>{b.phone}</div>
                  </td>
                  <td>
                    <div>{b.consultationDate}</div>
                    <div className={styles.tdSub}>{b.consultationTimeSlot}</div>
                  </td>
                  <td>{b.paymentAmount ? `₹${(b.paymentAmount / 100).toFixed(0)}` : '—'}</td>
                  <td><Badge label={b.status} /></td>
                  <td><Badge label={b.bookingSource || 'APP'} /></td>
                  <td>
                    {b.meetLink
                      ? <a href={b.meetLink} target="_blank" rel="noreferrer" className={styles.meetLink}>Join ↗</a>
                      : '—'}
                  </td>
                  <td>
                    <select
                      value={b.status}
                      disabled={updatingId === b.id}
                      onChange={e => handleStatusChange(b.id, e.target.value)}
                      className={styles.inlineSelect}
                    >
                      {BOOKING_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>
                    <button
                      onClick={() => handleDelete(b.id)}
                      disabled={deletingId === b.id}
                      className={styles.deleteBtn}
                    >
                      {deletingId === b.id ? '…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Contacts Panel ────────────────────────────────────────────────────────────

function ContactsPanel() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getAllContacts();
      setContacts(Array.isArray(data) ? data : []);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleProcess = async (id) => {
    setProcessingId(id);
    try {
      await adminService.markContactProcessed(id);
      setContacts(prev => prev.map(c => c.id === id ? { ...c, processed: true } : c));
    } catch {
      // silently ignore
    } finally {
      setProcessingId(null);
    }
  };

  const visible = showAll ? contacts : contacts.filter(c => !c.processed);
  const unprocessedCount = contacts.filter(c => !c.processed).length;

  if (loading) return <LoadingState text="Loading contacts…" />;

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Contact Submissions</h2>
        <div className={styles.filtersRow}>
          <span className={styles.countText}>{unprocessedCount} unprocessed</span>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            Show processed
          </label>
        </div>
      </div>

      {visible.length === 0 ? (
        <p className={styles.emptyState}>No contacts to show.</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email / Phone</th>
                <th>Subject</th>
                <th>Message</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(c => (
                <tr key={c.id} className={c.processed ? styles.dimRow : ''}>
                  <td className={styles.tdName}>{c.name}</td>
                  <td>
                    <div>{c.email}</div>
                    <div className={styles.tdSub}>{c.phone}</div>
                  </td>
                  <td>{c.subject}</td>
                  <td>
                    <div className={styles.tdTruncate} title={c.message}>{c.message}</div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td>
                    {c.processed ? (
                      <span className={styles.doneText}>✓ Done</span>
                    ) : (
                      <button
                        disabled={processingId === c.id}
                        onClick={() => handleProcess(c.id)}
                        className={styles.btnPrimary}
                      >
                        {processingId === c.id ? '…' : 'Mark Done'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Special Offer Detail Card ─────────────────────────────────────────────────

function SpecialOfferDetailCard({ offer: o, onClose }) {
  // Close on ESC key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const fmt = (ts) => ts ? new Date(ts).toLocaleString('en-IN') : '—';

  return (
    <div className={styles.detailOverlay} onClick={onClose}>
      <div className={styles.detailCard} onClick={e => e.stopPropagation()}>
        <div className={styles.detailHeader}>
          <h3 className={styles.detailTitle}>Purchase #{o.id} — {o.fullName}</h3>
          <button onClick={onClose} className={styles.editorClose} aria-label="Close">✕</button>
        </div>

        <div className={styles.detailGrid}>
          {/* Personal */}
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Full Name</span>
            <span className={styles.detailValue}>{o.fullName || '—'}</span>
          </div>
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>WhatsApp Number</span>
            <span className={styles.detailValue}>{o.whatsappNumber || '—'}</span>
          </div>
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Service</span>
            <span className={styles.detailValue}>{o.service || '—'}</span>
          </div>

          {/* Birth Details */}
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Date of Birth</span>
            <span className={styles.detailValue}>{o.dateOfBirth || '—'}</span>
          </div>
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Time of Birth</span>
            <span className={styles.detailValue}>{o.timeOfBirth || '—'}</span>
          </div>
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Place of Birth</span>
            <span className={styles.detailValue}>{o.placeOfBirth || '—'}</span>
          </div>

          {/* Payment */}
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Amount</span>
            <span className={styles.detailValue}>
              {o.amount ? `₹${(o.amount / 100).toFixed(0)}` : '—'}
            </span>
          </div>
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Payment Status</span>
            <span className={styles.detailValue}><Badge label={o.paymentStatus} /></span>
          </div>
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Razorpay Order ID</span>
            <span className={styles.detailValue} style={{ fontSize: 13, wordBreak: 'break-all' }}>
              {o.razorpayOrderId || '—'}
            </span>
          </div>

          {/* Status */}
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Offer Status</span>
            <span className={styles.detailValue}><Badge label={o.status} /></span>
          </div>

          {/* Timestamps */}
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Submitted</span>
            <span className={styles.detailValue} style={{ fontSize: 13 }}>{fmt(o.createdAt)}</span>
          </div>
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Updated</span>
            <span className={styles.detailValue} style={{ fontSize: 13 }}>{fmt(o.updatedAt)}</span>
          </div>
        </div>

        {/* Full question */}
        <div className={styles.detailQuestionBlock}>
          <div className={styles.detailQuestionLabel}>Question</div>
          <p className={styles.detailQuestionText}>
            {o.question
              ? o.question
              : (o.paymentCategory === 'WHATSAPP' || o.paymentCategory === 'TESTING')
                ? 'Question provided in whatsapp workflow'
                : '—'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Special Offers Panel ──────────────────────────────────────────────────────

function SpecialOffersPanel() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [filterMode, setFilterMode] = useState('ALL');
  const [filterDay,  setFilterDay]  = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo,   setFilterTo]   = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getAllSpecialOffers();
      setOffers(Array.isArray(data) ? data : []);
    } catch {
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sorted = [...offers].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  const visible = sorted.filter(o => {
    if (filterMode === 'ALL') return true;
    if (!o.createdAt) return false;
    const d = o.createdAt.slice(0, 10);
    if (filterMode === 'DAY')   return d === filterDay;
    if (filterMode === 'RANGE') {
      if (filterFrom && d < filterFrom) return false;
      if (filterTo   && d > filterTo)   return false;
      return true;
    }
    return true;
  });

  if (loading) return <LoadingState text="Loading special offers…" />;

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Special Offer Purchases</h2>
        <div className={styles.filtersRow}>
          <span className={styles.countText}>
            {visible.length} purchase{visible.length !== 1 ? 's' : ''}
          </span>

          <select
            value={filterMode}
            onChange={e => { setFilterMode(e.target.value); setFilterDay(''); setFilterFrom(''); setFilterTo(''); }}
            className={styles.select}
          >
            <option value="ALL">All Dates</option>
            <option value="DAY">Single Day</option>
            <option value="RANGE">Date Range</option>
          </select>

          {filterMode === 'DAY' && (
            <input
              type="date"
              value={filterDay}
              onChange={e => setFilterDay(e.target.value)}
              className={styles.dateInput}
            />
          )}

          {filterMode === 'RANGE' && (
            <>
              <input
                type="date"
                value={filterFrom}
                onChange={e => setFilterFrom(e.target.value)}
                className={styles.dateInput}
                placeholder="From"
              />
              <span className={styles.rangeSep}>–</span>
              <input
                type="date"
                value={filterTo}
                onChange={e => setFilterTo(e.target.value)}
                className={styles.dateInput}
                placeholder="To"
              />
            </>
          )}
        </div>
      </div>

      {visible.length === 0 ? (
        <p className={styles.emptyState}>No special offer purchases yet.</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>WhatsApp</th>
                <th>Service</th>
                <th>Category</th>
                <th>Question</th>
                <th>Payment</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(o => (
                <tr key={o.id} className={styles.clickableRow} onClick={() => setSelectedOffer(o)}>
                  <td className={styles.tdName}>{o.fullName}</td>
                  <td>{o.whatsappNumber}</td>
                  <td>{o.service}</td>
                  <td><Badge label={
                    o.paymentCategory === 'TESTING'  ? 'TESTING' :
                    o.paymentCategory === 'WHATSAPP' ? 'WHATSAPP' :
                    'LANDING PAGE'
                  } /></td>
                  <td>
                    <div className={styles.tdTruncate} title={o.question}>
                      {o.question
                        ? o.question
                        : (o.paymentCategory === 'WHATSAPP' || o.paymentCategory === 'TESTING')
                          ? 'Question provided in whatsapp workflow'
                          : '—'}
                    </div>
                  </td>
                  <td><Badge label={o.paymentStatus} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {o.createdAt ? new Date(o.createdAt).toLocaleDateString('en-IN') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedOffer && (
        <SpecialOfferDetailCard offer={selectedOffer} onClose={() => setSelectedOffer(null)} />
      )}
    </div>
  );
}

// ── Horoscope Editor ──────────────────────────────────────────────────────────

function todayStr() { return new Date().toISOString().slice(0, 10); }
function thisWeekStr() {
  const d = new Date();
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return d.toISOString().slice(0, 10);
}
function thisMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthToDate(m) { return m + '-01'; }
// Convert "YYYY-Www" to Monday ISO date
function weekInputToDate(weekStr) {
  if (!weekStr) return todayStr();
  const [year, week] = weekStr.split('-W').map(Number);
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
  return monday.toISOString().slice(0, 10);
}
// Convert ISO date to "YYYY-Www" for week input
function dateToWeekInput(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate + 'T00:00:00');
  const year = d.getFullYear();
  const startOfYear = new Date(year, 0, 1);
  const startDay = startOfYear.getDay() || 7;
  const startOfWeek1 = new Date(startOfYear);
  startOfWeek1.setDate(startOfYear.getDate() - startDay + 1);
  const week = Math.ceil(((d - startOfWeek1) / 86400000 + 1) / 7);
  return `${year}-W${String(week).padStart(2, '0')}`;
}

function HoroscopeEditor({ sign, onClose }) {
  const [period, setPeriod] = useState('DAILY');
  // Date inputs per period
  const [dateInputs, setDateInputs] = useState({
    DAILY: todayStr(),
    WEEKLY: dateToWeekInput(thisWeekStr()),
    MONTHLY: thisMonthStr(),
  });
  const [categories, setCategories] = useState({ ...EMPTY_CATS });
  const [pastReadings, setPastReadings] = useState([]);
  const [loadingPast, setLoadingPast] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  // Compute the canonical LocalDate string for the current input
  const getCanonicalDate = (p, inputs) => {
    if (p === 'DAILY') return inputs.DAILY;
    if (p === 'WEEKLY') return weekInputToDate(inputs.WEEKLY);
    return monthToDate(inputs.MONTHLY);
  };

  // Load past readings for this sign+period
  const loadPast = useCallback(async (p) => {
    setLoadingPast(true);
    try {
      const data = await adminService.getHoroscopesByPeriod(sign, p);
      setPastReadings(Array.isArray(data) ? data : []);
    } catch {
      setPastReadings([]);
    } finally {
      setLoadingPast(false);
    }
  }, [sign]);

  // Load reading for selected date
  const loadForDate = useCallback((p, inputs, readings) => {
    const target = getCanonicalDate(p, inputs);
    const match = readings.find(r => r.periodDate === target);
    setUpdatedAt(match ? match.updatedAt : null);
    if (match && match.content) {
      try {
        const parsed = JSON.parse(match.content);
        setCategories({ ...EMPTY_CATS, ...parsed });
      } catch {
        setCategories({ ...EMPTY_CATS });
      }
    } else {
      setCategories({ ...EMPTY_CATS });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPast(true);
      try {
        const data = await adminService.getHoroscopesByPeriod(sign, period);
        if (!cancelled) {
          const list = Array.isArray(data) ? data : [];
          setPastReadings(list);
          loadForDate(period, dateInputs, list);
        }
      } catch {
        if (!cancelled) setPastReadings([]);
      } finally {
        if (!cancelled) setLoadingPast(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sign, period]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateChange = (val) => {
    const newInputs = { ...dateInputs, [period]: val };
    setDateInputs(newInputs);
    loadForDate(period, newInputs, pastReadings);
    setSaved(false);
  };

  const handlePeriodChange = async (p) => {
    setPeriod(p);
    setCategories({ ...EMPTY_CATS });
    setUpdatedAt(null);
    setSaved(false);
    await loadPast(p);
  };

  // When past readings are freshly loaded, auto-populate content for selected date
  useEffect(() => {
    if (pastReadings.length >= 0) {
      loadForDate(period, dateInputs, pastReadings);
    }
  }, [pastReadings]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePickPast = (reading) => {
    // Set date input to the past reading's date
    let newDateInput = reading.periodDate;
    if (period === 'WEEKLY') newDateInput = dateToWeekInput(reading.periodDate);
    if (period === 'MONTHLY') newDateInput = reading.periodDate.slice(0, 7);
    setDateInputs(prev => ({ ...prev, [period]: newDateInput }));
    setUpdatedAt(reading.updatedAt);
    setSaved(false);
    if (reading.content) {
      try {
        const parsed = JSON.parse(reading.content);
        setCategories({ ...EMPTY_CATS, ...parsed });
      } catch {
        setCategories({ ...EMPTY_CATS });
      }
    } else {
      setCategories({ ...EMPTY_CATS });
    }
  };

  const handleSave = async () => {
    const periodDate = getCanonicalDate(period, dateInputs);
    setSaving(true);
    try {
      const res = await adminService.updateHoroscope(sign, period, JSON.stringify(categories), periodDate);
      setUpdatedAt(res.updatedAt);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      // Refresh past list
      const updated = await adminService.getHoroscopesByPeriod(sign, period);
      setPastReadings(Array.isArray(updated) ? updated : []);
    } catch {
      // silently ignore
    } finally {
      setSaving(false);
    }
  };

  const signDisplay = sign.charAt(0) + sign.slice(1).toLowerCase();

  const formatPeriodDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate + 'T00:00:00');
    if (period === 'DAILY') return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    if (period === 'WEEKLY') return `Week of ${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
    return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  return (
    <div className={styles.editorCard}>
      <div className={styles.editorHeader}>
        <h3 className={styles.editorTitle}>
          {ZODIAC_SYMBOLS[sign]} {signDisplay}
        </h3>
        <button onClick={onClose} className={styles.editorClose} aria-label="Close">✕</button>
      </div>

      {/* Period tabs */}
      <div className={styles.periodTabs}>
        {PERIODS.map(p => (
          <button
            key={p}
            onClick={() => handlePeriodChange(p)}
            className={`${styles.periodTab} ${period === p ? styles.periodTabActive : ''}`}
          >
            {p.charAt(0) + p.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {/* Date picker */}
      <div className={styles.datepickerRow}>
        <label className={styles.datepickerLabel}>
          {period === 'DAILY' ? 'Date' : period === 'WEEKLY' ? 'Week' : 'Month'}:
        </label>
        {period === 'DAILY' && (
          <input type="date" className={styles.dateInput}
            value={dateInputs.DAILY}
            onChange={e => handleDateChange(e.target.value)} />
        )}
        {period === 'WEEKLY' && (
          <input type="week" className={styles.dateInput}
            value={dateInputs.WEEKLY}
            onChange={e => handleDateChange(e.target.value)} />
        )}
        {period === 'MONTHLY' && (
          <input type="month" className={styles.dateInput}
            value={dateInputs.MONTHLY}
            onChange={e => handleDateChange(e.target.value)} />
        )}
      </div>

      {/* Editor — 6 category textareas */}
      {CATEGORY_LABELS.map(({ key, label }) => (
        <div key={key} style={{ marginBottom: 16 }}>
          <label className={styles.datepickerLabel} style={{ display: 'block', marginBottom: 6 }}>
            {label}
          </label>
          <textarea
            className={styles.textarea}
            rows={3}
            value={categories[key]}
            onChange={e => { setCategories(prev => ({ ...prev, [key]: e.target.value })); setSaved(false); }}
            placeholder={`${label} reading…`}
          />
        </div>
      ))}
      <div className={styles.editorFooter}>
        <span className={styles.lastSaved}>
          {updatedAt
            ? `Last saved: ${new Date(updatedAt).toLocaleString('en-IN')}`
            : 'No reading saved for this date yet'}
        </span>
        <button onClick={handleSave} disabled={saving} className={styles.btnSave}>
          {saving ? 'Saving…' : saved ? '✓ Saved!' : 'Save'}
        </button>
      </div>

      {/* Past readings list */}
      <div className={styles.pastReadings}>
        <h4 className={styles.pastReadingsTitle}>
          Recent {period.charAt(0) + period.slice(1).toLowerCase()} Readings
        </h4>
        {loadingPast ? (
          <p className={styles.pastLoadingText}>Loading…</p>
        ) : pastReadings.length === 0 ? (
          <p className={styles.pastEmptyText}>No readings saved yet.</p>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{period === 'DAILY' ? 'Date' : period === 'WEEKLY' ? 'Week' : 'Month'}</th>
                  <th>Preview</th>
                  <th>Last Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pastReadings.map(r => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {formatPeriodDate(r.periodDate)}
                    </td>
                    <td>
                      <div className={styles.tdTruncate} style={{ maxWidth: 260 }}>
                        {(() => {
                          try {
                            const p = JSON.parse(r.content);
                            return p.finance || p.career || r.content || <em style={{ color: '#aaa' }}>Empty</em>;
                          } catch {
                            return r.content || <em style={{ color: '#aaa' }}>Empty</em>;
                          }
                        })()}
                      </div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 11, color: '#888' }}>
                      {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td>
                      <button className={styles.btnPrimary} onClick={() => handlePickPast(r)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Horoscope Panel ───────────────────────────────────────────────────────────

function HoroscopePanel() {
  const [selectedSign, setSelectedSign] = useState(null);

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Horoscope Settings</h2>
      </div>
      <p className={styles.horoscopeIntro}>
        Select a zodiac sign to edit its Daily, Weekly, or Monthly reading. Changes are reflected live on the website.
      </p>
      <div className={styles.zodiacGrid}>
        {ZODIAC_SIGNS.map(sign => (
          <button
            key={sign}
            onClick={() => setSelectedSign(selectedSign === sign ? null : sign)}
            className={`${styles.zodiacCard} ${selectedSign === sign ? styles.zodiacCardActive : ''}`}
          >
            <span className={styles.zodiacSymbol}>{ZODIAC_SYMBOLS[sign]}</span>
            <span className={styles.zodiacName}>{sign.charAt(0) + sign.slice(1).toLowerCase()}</span>
          </button>
        ))}
      </div>

      {selectedSign && (
        <HoroscopeEditor sign={selectedSign} onClose={() => setSelectedSign(null)} />
      )}
    </div>
  );
}

// ── Main Admin Page ───────────────────────────────────────────────────────────

const CONSULTATION_TABS = ['Bookings', 'Contacts', 'Special Offers'];

export default function AdminPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState('consultation');
  const [consultationTab, setConsultationTab] = useState('Bookings');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !user || !user.roles?.includes('ADMIN')) {
      navigate('/');
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.roles?.includes('ADMIN')) {
      adminService.getStats().then(setStats).catch(() => {});
    }
  }, [isAuthenticated, user]);

  if (!isAuthenticated || !user || !user.roles?.includes('ADMIN')) return null;

  return (
    <div className={styles.page}>
      <Header activePage="" />

      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.pageHeader}>
            {stats
              ? <div className={styles.statCard}>
                  <span className={styles.statValue}>{stats.totalUsers}</span>
                  <span className={styles.statLabel}>Total Users</span>
                </div>
              : <div />}
            <div className={styles.pageTitleBlock}>
              <h1 className={styles.pageTitle}>Admin Panel</h1>
              <p className={styles.pageSubtitle}>Manage bookings, contacts, and horoscope content</p>
            </div>
            {stats
              ? <div className={styles.statCard}>
                  <span className={styles.statValue}>{stats.totalCustomers}</span>
                  <span className={styles.statLabel}>Total Customers</span>
                </div>
              : <div />}
          </div>

          {/* Main Tabs */}
          <div className={styles.mainTabs}>
            <button
              className={`${styles.mainTab} ${mainTab === 'consultation' ? styles.mainTabActive : ''}`}
              onClick={() => setMainTab('consultation')}
            >
              Consultation
            </button>
            <button
              className={`${styles.mainTab} ${mainTab === 'horoscope' ? styles.mainTabActive : ''}`}
              onClick={() => setMainTab('horoscope')}
            >
              Horoscope Settings
            </button>
          </div>

          {/* Consultation Tab */}
          {mainTab === 'consultation' && (
            <div>
              <div className={styles.subTabs}>
                {CONSULTATION_TABS.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setConsultationTab(tab)}
                    className={`${styles.subTab} ${consultationTab === tab ? styles.subTabActive : ''}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <div className={styles.card}>
                {consultationTab === 'Bookings' && <BookingsPanel />}
                {consultationTab === 'Contacts' && <ContactsPanel />}
                {consultationTab === 'Special Offers' && <SpecialOffersPanel />}
              </div>
            </div>
          )}

          {/* Horoscope Tab */}
          {mainTab === 'horoscope' && (
            <div className={styles.card}>
              <HoroscopePanel />
            </div>
          )}
        </div>
      </main>

    </div>
  );
}
